import {
  EMarketingCampaignStatus,
  EMarketingChannel,
  EMarketingConsentStatus,
  EMarketingContactEventType,
  EMarketingContactSource,
  EMarketingEnrollmentStatus,
  EMarketingFlowStatus,
  EMarketingFlowTrigger,
  EMarketingSkipReason,
  EMarketingTemplateStatus
} from "../marketing.types";

export * from "./enums";

/**
 * Indicative rate for one APPROVED TEMPLATE message — a label, not a
 * calculation. Free-form text inside a contact's 24h window and in-app push
 * cost nothing, which is why a campaign's real figure comes from the backend
 * estimate (`useCampaignEstimate`) and never from multiplying an audience.
 */
export const WHATSAPP_TEMPLATE_MESSAGE_COST_FCFA = 15.5;

export const TEMPLATE_NAME_LOCK_WEEKS = 4;

export const MARKETING_CONTACTS_PAGE_SIZE = 50;

export const CHANNEL_LABELS: Record<EMarketingChannel, string> = {
  [EMarketingChannel.WhatsApp]: "WhatsApp",
  [EMarketingChannel.Push]: "Push"
};

export const CHANNEL_COLORS: Record<EMarketingChannel, string> = {
  [EMarketingChannel.WhatsApp]: "#16C350",
  [EMarketingChannel.Push]: "#4A6CF7"
};

export const CONSENT_STATUS_LABELS: Record<EMarketingConsentStatus, string> = {
  [EMarketingConsentStatus.Unknown]: "Unknown",
  [EMarketingConsentStatus.OptedIn]: "Opted in",
  [EMarketingConsentStatus.OptedOut]: "Opted out"
};

export const CONSENT_STATUS_COLORS: Record<EMarketingConsentStatus, string> = {
  [EMarketingConsentStatus.Unknown]: "#8E8E93",
  [EMarketingConsentStatus.OptedIn]: "#16C350",
  [EMarketingConsentStatus.OptedOut]: "#FF0033"
};

export const CONTACT_SOURCE_LABELS: Record<EMarketingContactSource, string> = {
  [EMarketingContactSource.CtwaAd]: "CTWA ad",
  [EMarketingContactSource.WhatsApp]: "WhatsApp",
  [EMarketingContactSource.Order]: "Order",
  [EMarketingContactSource.ResellerSignup]: "App signup",
  [EMarketingContactSource.AppInstall]: "App install"
};

export const CONTACT_EVENT_TYPE_LABELS: Record<
  EMarketingContactEventType,
  string
> = {
  [EMarketingContactEventType.ProspectEntered]: "Prospect entered",
  [EMarketingContactEventType.WhatsappOrderStarted]: "WhatsApp order started",
  [EMarketingContactEventType.CartCreated]: "Cart created",
  [EMarketingContactEventType.OrderPlaced]: "Order placed",
  [EMarketingContactEventType.OrderDelivered]: "Order delivered",
  [EMarketingContactEventType.AppOpen]: "App open",
  [EMarketingContactEventType.ProductView]: "Product view",
  [EMarketingContactEventType.MessageSent]: "Message sent",
  [EMarketingContactEventType.ConsentOptIn]: "Opted in",
  [EMarketingContactEventType.ConsentOptOut]: "Opted out",
  [EMarketingContactEventType.ConsentAsked]: "Asked for consent",
  [EMarketingContactEventType.ConsentDeclined]: "Declined marketing"
};

export const TEMPLATE_STATUS_LABELS: Record<EMarketingTemplateStatus, string> =
  {
    [EMarketingTemplateStatus.Draft]: "Draft",
    [EMarketingTemplateStatus.Pending]: "Pending",
    [EMarketingTemplateStatus.Approved]: "Approved",
    [EMarketingTemplateStatus.Rejected]: "Rejected"
  };

export const TEMPLATE_STATUS_COLORS: Record<EMarketingTemplateStatus, string> =
  {
    [EMarketingTemplateStatus.Draft]: "#8E8E93",
    [EMarketingTemplateStatus.Pending]: "#FF9900",
    [EMarketingTemplateStatus.Approved]: "#16C350",
    [EMarketingTemplateStatus.Rejected]: "#FF0033"
  };

export const CAMPAIGN_STATUS_LABELS: Record<EMarketingCampaignStatus, string> =
  {
    [EMarketingCampaignStatus.Draft]: "Draft",
    [EMarketingCampaignStatus.Running]: "Running",
    [EMarketingCampaignStatus.Completed]: "Completed",
    [EMarketingCampaignStatus.Failed]: "Failed"
  };

export const CAMPAIGN_STATUS_COLORS: Record<EMarketingCampaignStatus, string> =
  {
    [EMarketingCampaignStatus.Draft]: "#8E8E93",
    [EMarketingCampaignStatus.Running]: "#FF9900",
    [EMarketingCampaignStatus.Completed]: "#16C350",
    [EMarketingCampaignStatus.Failed]: "#FF0033"
  };

export const FLOW_TRIGGER_LABELS: Record<EMarketingFlowTrigger, string> = {
  [EMarketingFlowTrigger.Manual]: "Manual",
  [EMarketingFlowTrigger.ResellerSignup]: "App signup",
  [EMarketingFlowTrigger.OrderPlaced]: "Order placed",
  [EMarketingFlowTrigger.ProspectEntered]: "Prospect entered"
};

export const FLOW_STATUS_LABELS: Record<EMarketingFlowStatus, string> = {
  [EMarketingFlowStatus.Active]: "Active",
  [EMarketingFlowStatus.Paused]: "Paused"
};

export const FLOW_STATUS_COLORS: Record<EMarketingFlowStatus, string> = {
  [EMarketingFlowStatus.Active]: "#16C350",
  [EMarketingFlowStatus.Paused]: "#FF9900"
};

export const ENROLLMENT_STATUS_LABELS: Record<
  EMarketingEnrollmentStatus,
  string
> = {
  [EMarketingEnrollmentStatus.Active]: "Active",
  [EMarketingEnrollmentStatus.Completed]: "Completed",
  [EMarketingEnrollmentStatus.Stopped]: "Stopped"
};

export const ENROLLMENT_STATUS_COLORS: Record<
  EMarketingEnrollmentStatus,
  string
> = {
  [EMarketingEnrollmentStatus.Active]: "#16C350",
  [EMarketingEnrollmentStatus.Completed]: "#8E8E93",
  [EMarketingEnrollmentStatus.Stopped]: "#FF0033"
};

export const SKIP_REASON_LABELS: Record<EMarketingSkipReason, string> = {
  [EMarketingSkipReason.ContactNotFound]: "Contact not found",
  [EMarketingSkipReason.OptedOut]: "Opted out",
  [EMarketingSkipReason.ChannelNotAvailable]: "Channel not available",
  [EMarketingSkipReason.FrequencyCapped]: "Frequency capped",
  [EMarketingSkipReason.SendFailed]: "Send failed",
  [EMarketingSkipReason.CodRefusalFuseBlown]: "Paused (high delivery-refusal rate)",
  [EMarketingSkipReason.SaleInProgress]: "Sale in progress",
  [EMarketingSkipReason.ConsentRequired]: "No marketing opt-in",
  [EMarketingSkipReason.OutsideWindow]: "Outside the 24h window (no template)"
};
